/** D1 column names with cross-table reuse -- the audit-stamp pair and status columns authored as raw strings
 * across 100+ SQL `SET` / `WHERE` template sites, where a column rename would otherwise skip every callsite and
 * silently break queries. Paired ESLint rule `custom/no-d1-column-name-literals` flags these literals inside SQL
 * template-literal contexts. Cross-table collision: `transaction_status` is a `ledger` column while
 * `market_maker_transaction_status` is a DISTINCT `market_maker` column, so both need separate entries.
 */

// Imported RELATIVELY on purpose: this leaf is in the root vitest configs' config-load closure, where a `@federa/*`
// specifier reads MAIN on a worktree; the closure's relativity walk (const/__tests__/vitest-workerd-tier.test.ts) pins it.
import { wordBoundaryRegex } from './regex-escape.ts';

/** NOT form-field names (use `FORM_FIELDS`). Keys mirror the SQL column names in SCREAMING_SNAKE_CASE; values are the
 * literals as they appear in the migration chain.
 *
 * Membership here is an ENFORCEMENT decision, not a taxonomy: `custom/no-d1-column-name-literals` derives its
 * `TARGET_LITERALS` from `Object.values` of this record, so adding a member makes every raw occurrence of that literal
 * inside a SQL template a lint error. The line this docstring used to draw against `DB_COLUMNS` ("identity columns
 * live there") is not the real one and never was -- `MODIFIED_AT` is here while `CREATED_AT` is there, both universal
 * audit stamps. `const/db-columns.ts` carries the reasoning and the fold hazard. */
export const DB_COLUMN_NAMES = {
	/** Universal audit timestamp -- present on nearly every D1 table. */
	MODIFIED_AT: 'modified_at',
	/** `services.credential_policy` -- named by all three status-enum parity registries, which each indexed it by literal. */
	CREDENTIAL_POLICY: 'credential_policy',
	/** `user_services.pricing_model`, mirrored onto `orders.pricing_model`. Named by all three parity registries AND read off an orders row by `flows/order-complete.ts`. */
	PRICING_MODEL: 'pricing_model',
	/** US state FK, carried by the navigator draft-update column list and the shared valid-column allowlist. */
	STATE_ID: 'state_id',
	/** Audit lineage stamp -- names the flow that performed the last write. */
	MODIFYING_FLOW: 'modifying_flow',
	/** Ledger row transaction status (LEDGER_STATUS values). */
	TRANSACTION_STATUS: 'transaction_status',
	/** Market-maker copy of the ledger row's transaction status. A DISTINCT column from `transaction_status` -- DO NOT collapse: the prefix is part of the column name on the `market_maker` table. */
	MARKET_MAKER_TRANSACTION_STATUS: 'market_maker_transaction_status',
	/** Twilio Message SID correlation key, mirrored onto the `notifications` table. */
	TWILIO_MESSAGE_SID: 'twilio_message_sid',
	/** The profile FK, and the widest cross-table column in the schema: `licenses`, `ledger`, `ledger_transactions`,
	 *  `team_members`, `orders` (as `buyer_profile_id` / `provider_profile_id`), `user_services` and `phone` all key on
	 *  it. Verified against `migrations/d1/contacts/0001_contacts_core.sql` and
	 *  `migrations/d1/storage-gateway/20260814_marketplace_generalization_schema.sql`. */
	PROFILE_ID: 'profile_id',
	/** `profiles.phone_number`, mirrored onto `group_phone_numbers` and the connect-service phone table. Verified
	 *  against `migrations/d1/storage-gateway/20260821_group_phone_numbers.sql`. DISTINCT from {@link DB_COLUMN_NAMES.MFA_PHONE}
	 *  -- the two are separate columns of the SAME profiles row and the lookups COALESCE across them, so collapsing
	 *  them would make a phone lookup read the wrong number. */
	PHONE_NUMBER: 'phone_number',
	/** `profiles.mfa_phone` -- the verified second-factor number, read as the first arm of every
	 *  `COALESCE(mfa_phone, phone_number)` profile lookup. Verified against the live schema's `profiles` DDL. */
	MFA_PHONE: 'mfa_phone',
	/** Given name, carried by BOTH `profiles` and `people`. Same column name on two tables, which is exactly the
	 *  cross-table reuse this record exists for: a rename that skipped one of the two projections would read a null
	 *  name and render an unnamed party on a title document. */
	FIRST_NAME: 'first_name',
	/** Family name, the `people` / `profiles` pair of {@link DB_COLUMN_NAMES.FIRST_NAME}. */
	LAST_NAME: 'last_name',
	/** `profiles.partner_status` -- a CHECK-constrained partner lifecycle column, so a rename that skipped a callsite
	 *  would write into a column with no constraint and silently accept an unmodelled value. */
	PARTNER_STATUS: 'partner_status',
	/** `profiles.smartagent_status`, the CHECK-constrained sibling of {@link DB_COLUMN_NAMES.PARTNER_STATUS}. */
	SMARTAGENT_STATUS: 'smartagent_status',
	/** `profiles.payout_account` -- CHECK-constrained payout-onboarding state, and a MONEY-ADJACENT column: a rename
	 *  that skipped a read would report every profile at the `'No Account'` default and gate payouts off. */
	PAYOUT_ACCOUNT: 'payout_account',
	/** `profiles.approval_limit` -- the REAL-valued approval ceiling a money gate compares against, so a rename
	 *  skipping a callsite reads null and the comparison silently stops gating. */
	APPROVAL_LIMIT: 'approval_limit',
} as const;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): reverse-lookup of the SSOT KEY whose value is `literal`, so a
 * violation message can name `DB_COLUMN_NAMES.<KEY>` as the remediation accessor. Falls back to the uppercased
 * literal when no entry owns it, which keeps a message readable rather than empty.
 *
 * Homed beside the record it folds -- both governing matchers (the `QUAL-NO-D1-COLUMN-NAME-LITERALS-001` rule leaf and
 * the paired `custom/no-d1-column-name-literals` ESLint rule) already value-import this leaf, so the shared body needs
 * no imports of its own and auto-tracks SSOT growth.
 */
export function ssotKeyForLiteral(literal: string): string {
	for (const [key, value] of Object.entries(DB_COLUMN_NAMES)) {
		if (value === literal) return key;
	}
	return literal.toUpperCase();
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the `modifying_flow` COLUMN as a word-bounded token, DERIVED from
 * {@link DB_COLUMN_NAMES} rather than spelled, so renaming the column here retargets both matchers instead of leaving
 * two hand-written copies enforcing the old name.
 *
 * Both audit-stamp rules test a comma-split INSERT column list for this column -- `QUAL-MODIFYING-FLOW-REQUIRED-001`
 * (the column is MISSING) and `QUAL-HANDLER-MODIFYING-FLOW-SSOT-001` (it is present but bound to a literal) -- and each
 * had spelled the pattern inline. The word boundaries are what stop a longer column merely CONTAINING the name from
 * matching; the value carries no regex metacharacters, so no escaping is needed to interpolate it.
 *
 * Flagless and `.test`-only, so the compiled instance is shared safely.
 */
export const MODIFYING_FLOW_COLUMN_TOKEN_REGEX = wordBoundaryRegex(DB_COLUMN_NAMES.MODIFYING_FLOW);

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the same column in EITHER spelling a column list may carry -- the
 * bare snake_case literal, or the `DB_COLUMN_NAMES.MODIFYING_FLOW` ACCESSOR a `${TABLES.X}` template statement
 * interpolates. Both name the identical column, so a presence check that reads only the first convicts correct code.
 *
 * Why this is a sibling and not a widening of {@link MODIFYING_FLOW_COLUMN_TOKEN_REGEX}: that regex answers "is this
 * comma-split column-list MEMBER the modifying_flow column", where the accessor arm is the SAME question; but it is
 * also the token whose ABSENCE a rule reports, and widening it in place would silently retarget both consumers at once.
 * The accessor arm is spelled from the member NAME, so renaming the const member retargets this matcher too.
 */
export const MODIFYING_FLOW_COLUMN_PRESENT_REGEX = new RegExp(
	`\\b${DB_COLUMN_NAMES.MODIFYING_FLOW}\\b|\\bDB_COLUMN_NAMES\\.MODIFYING_FLOW\\b`,
);
